import { NavLink, Outlet, useNavigate, useLocation, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SupportBanner } from "@/components/SupportBanner";
import { GlobalAdminBanner } from "@/components/GlobalAdminBanner";
import { LicenseExpiryBanner } from "@/components/LicenseExpiryBanner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutDashboard, Truck, Package, Users, MapPin, Map, Tag, CreditCard, FileText, LogOut, Cloud, History, Building2, ScrollText, Menu, KeyRound, Bell, Wrench, AlertTriangle, Archive, ChevronLeft, ChevronRight, Kanban, ShieldCheck, Radar, Smartphone, MessageCircle, Route, HandCoins } from "lucide-react";
import { ensurePushSubscription, sendTestNotification } from "@/lib/push";
import { isAdminLike, isUserManager, hasPerm } from "@/lib/roles";

// Itens do sidebar do TENANT. `resource` é a chave da PERM_RESOURCES/perms_matrix.
// Se `resource` presente, item só aparece se `hasPerm(user, resource, "v")` for true.
const tenantItems = [
  { to: "", label: "Dashboard", icon: LayoutDashboard, end: true, resource: "dashboard" },
  { to: "entregas", label: "Entregas", icon: Package, resource: "entregas" },
  { to: "kanban", label: "Kanban", icon: Kanban, resource: "kanban" },
  { to: "historico", label: "Histórico", icon: History, resource: "entregas" },
  { to: "acerto", label: "Acerto", icon: HandCoins, resource: "acertos" },
  { to: "entregadores", label: "Entregadores", icon: Truck, resource: "entregadores" },
  { to: "bairros", label: "Bairros", icon: MapPin, resource: "bairros" },
  { to: "rotas", label: "Rotas", icon: Route, resource: "rotas" },
  { to: "regioes", label: "Regiões", icon: Map, resource: "regioes" },
  { to: "tipos", label: "Tipos de Entrega", icon: Tag, resource: "tipos_entrega" },
  { to: "formas-pgto", label: "Formas Pgto", icon: CreditCard, resource: "formas_pgto" },
  { to: "usuarios", label: "Usuários", icon: Users, resource: "users", userManager: true },
  { to: "configuracao", label: "Empresa", icon: Building2, resource: "empresas" },
  { to: "relatorios", label: "Relatórios", icon: FileText, resource: "relatorios" },
  { to: "logistica", label: "Logística", icon: MapPin, resource: "logistica" },
  { to: "monitoramento", label: "Monitoramento", icon: Radar, resource: "monitoramento", featureFlag: "rastreamento_gps_ativo" },
  { to: "auditoria", label: "Auditoria", icon: ScrollText, resource: "auditoria" },
  { to: "whatsapp-logs", label: "WhatsApp", icon: MessageCircle, resource: "whatsapp_logs" },
  { to: "backup", label: "Backup", icon: Cloud, resource: "backup" },
];

// Itens do painel ADMIN GLOBAL (fora do escopo de tenant, apenas Super Admin). `to` absoluto.
const adminItems = [
  { to: "/admin/empresas", label: "Empresas", icon: Building2, end: true },
  { to: "/admin/data-repair", label: "Reparar Dados", icon: Wrench, end: true },
  { to: "/admin/mobile-logs", label: "Logs Mobile", icon: AlertTriangle, end: true },
  { to: "/admin/backup-vault", label: "Cofre de Backups", icon: Archive, end: true },
  { to: "/admin/security", label: "Segurança (2FA)", icon: ShieldCheck, end: true },
  { to: "/admin/mobile-app", label: "App Android", icon: Smartphone, end: true },
];

function EmpresaSwitcher({ compact = false }) {
  const { empresas, currentEmpresaId, switchEmpresa, user } = useAuth();
  if (!empresas || empresas.length === 0) return null;
  const canSelect = isAdminLike(user) || (user?.permissions || []).includes("select_empresa");
  // Sem permissão: mostra apenas o nome (readonly), sem dropdown
  if (!canSelect) {
    const cur = empresas.find((e) => e.id === currentEmpresaId);
    if (!cur) return null;
    return (
      <div className={compact ? "flex-1 min-w-0" : "flex items-center gap-2"} data-testid="empresa-locked">
        {!compact && <span className="text-xs text-muted-foreground">Empresa:</span>}
        <span className={`inline-flex items-center h-8 px-2 rounded border border-orange-200 bg-orange-50/60 text-xs font-semibold truncate ${compact ? "w-full" : "min-w-[180px]"}`}>
          {cur.nome}
        </span>
      </div>
    );
  }
  return (
    <div className={compact ? "flex-1 min-w-0" : "flex items-center gap-2"} data-testid="empresa-switcher">
      {!compact && <span className="text-xs text-muted-foreground">Empresa:</span>}
      <Select value={currentEmpresaId || ""} onValueChange={(v) => switchEmpresa(v)}>
        <SelectTrigger className={`${compact ? "h-8 text-xs" : "h-9 min-w-[180px]"} border-orange-200 bg-orange-50/60`} data-testid="empresa-select">
          <SelectValue placeholder="Selecionar empresa" />
        </SelectTrigger>
        <SelectContent>
          {empresas.map((e) => (
            <SelectItem key={e.id} value={e.id} data-testid={`empresa-opt-${e.id}`}>{e.nome}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SidebarContent({ empresa, user, isAdmin, isUM, onNavigate, onLogout, isAdminScope, slug, isCollapsed = false, onToggle }) {
  const isSA = !user?._impersonating && (user?.role === "super_admin" || (user?.role === "admin" && !(user?.empresa_ids || []).length));
  // Rastreamento GPS efetivo = SA liberou (default true) E empresa ativou.
  // Espelha `is_tracking_effectively_on(emp)` do backend (fev/2026).
  const trackingOn = empresa?.rastreamento_gps_liberado !== false && !!empresa?.rastreamento_gps_ativo;
  const visibleItems = useMemo(() => {
    if (isAdminScope) return isSA ? adminItems : [];
    // Usuário deve ter matrix válida com AO MENOS uma view=true pra ver o sidebar
    const matrix = user?.perms_matrix;
    const hasAnyMatrixView = !isSA && matrix && typeof matrix === "object"
      && Object.values(matrix).some((r) => r?.v);
    return tenantItems.filter((item) => {
      if (item.userManager && !isUM) return false;
      // Feature gate: menu "Monitoramento" só aparece se a empresa ligou
      // `rastreamento_gps_ativo` (fev/2026). Super Admin também respeita
      // — quando vê uma empresa, só vê o que ela contratou.
      if (item.featureFlag === "rastreamento_gps_ativo" && !trackingOn) return false;
      if (isSA) return true;
      // Itens sem resource (Dashboard, Logística) — só aparecem se o user tem AO MENOS uma perm view
      if (!item.resource && item.alwaysAdmin) return hasAnyMatrixView;
      if (item.resource) return hasPerm(user, item.resource, "v");
      return false;
    });
  }, [user, isUM, isSA, isAdminScope, trackingOn]);
  const buildTo = (item) => isAdminScope ? item.to : (slug ? `/${slug}/${item.to}`.replace(/\/$/, "") || `/${slug}/` : `/${item.to}`);
  return (
    <>
      <div className={`border-b border-orange-100 flex items-center ${isCollapsed ? "flex-col gap-2 p-3" : "gap-2 p-5 justify-between"}`}>
        <div className={`flex items-center gap-2 min-w-0 ${isCollapsed ? "justify-center" : ""}`}>
          {empresa?.logo_base64 ? (
            <img src={empresa.logo_base64} alt="logo" className="w-10 h-10 object-contain rounded flex-shrink-0" data-testid="sidebar-logo" />
          ) : (
            <Truck className="w-6 h-6 text-orange-600 flex-shrink-0" />
          )}
          {!isCollapsed && (
            <div className="min-w-0">
              <div className="font-extrabold text-lg leading-tight truncate max-w-[10rem]" data-testid="sidebar-empresa-nome">{empresa?.nome || (isAdminScope ? "Painel Global" : "Entregas")}</div>
              <div className="text-[10px] uppercase tracking-widest text-orange-600 truncate max-w-[10rem]">{empresa?.segmento || (isAdminScope ? "SaaS · Super Admin" : "Farmácia")}</div>
            </div>
          )}
        </div>
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            title={isCollapsed ? "Expandir menu" : "Recolher menu"}
            aria-label={isCollapsed ? "Expandir menu" : "Recolher menu"}
            data-testid="sidebar-toggle"
            className="p-1.5 rounded-md hover:bg-orange-100 text-slate-600 hover:text-orange-700 transition flex-shrink-0"
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4"/> : <ChevronLeft className="w-4 h-4"/>}
          </button>
        )}
      </div>
      {!isAdminScope && !isCollapsed && (
        <div className="px-3 pt-3">
          <EmpresaSwitcher />
        </div>
      )}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto overflow-x-hidden">
        {visibleItems.map((i) => (
          <NavLink key={i.to || "index"} to={buildTo(i)} end={i.end} data-testid={`nav-${i.to || "home"}`}
            onClick={onNavigate}
            title={isCollapsed ? i.label : undefined}
            className={({ isActive }) => `flex items-center gap-3 ${isCollapsed ? "justify-center px-2" : "px-3"} py-2 rounded-lg text-sm font-medium transition ${isActive ? "bg-orange-600 text-white" : "text-slate-700 hover:bg-orange-100"}`}>
            <i.icon className="w-4 h-4 flex-shrink-0" />
            {!isCollapsed && <span className="truncate">{i.label}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-orange-100">
        {!isCollapsed && (
          <div className="px-2 pb-2 text-xs">
            <div className="font-semibold truncate">{user?.nome}</div>
            <div className="text-muted-foreground truncate">{user?.email} · <span className="uppercase text-orange-600">{user?.role}</span></div>
          </div>
        )}
        <div className={`flex ${isCollapsed ? "flex-col" : ""} gap-1`}>
          <Button data-testid="btn-notif" variant="ghost" size="sm" className={`${isCollapsed ? "w-full h-9 px-0" : "flex-1"} border border-orange-100`}
            title={isCollapsed ? "Alertas" : undefined}
            onClick={async () => {
              const r = await ensurePushSubscription();
              if (r?.ok) {
                try { await sendTestNotification(); toast.success("Notificação enviada"); }
                catch { toast.success("Notificações ativadas"); }
              } else if (r?.reason === "denied") toast.error("Permissão negada nas configurações do navegador");
              else if (r?.reason === "unsupported") toast.error("Navegador não suporta notificações");
              else toast.error("Não foi possível ativar as notificações");
            }}>
            <Bell className={`w-4 h-4 ${isCollapsed ? "" : "mr-1"}`} />{!isCollapsed && "Alertas"}
          </Button>
          <Button data-testid="btn-change-pwd" variant="ghost" size="sm" className={`${isCollapsed ? "w-full h-9 px-0" : "flex-1"} border border-orange-100`}
            title={isCollapsed ? "Alterar senha" : undefined}
            onClick={() => window.dispatchEvent(new Event("open-change-password"))}>
            <KeyRound className={`w-4 h-4 ${isCollapsed ? "" : "mr-1"}`} />{!isCollapsed && "Senha"}
          </Button>
          <Button data-testid="btn-logout" variant="outline" size="sm" className={`${isCollapsed ? "w-full h-9 px-0" : "flex-1"}`}
            title={isCollapsed ? "Sair" : undefined}
            onClick={onLogout}>
            <LogOut className={`w-4 h-4 ${isCollapsed ? "" : "mr-1"}`} />{!isCollapsed && "Sair"}
          </Button>
        </div>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout, currentEmpresaId } = useAuth();
  const nav = useNavigate();
  const { slug } = useParams();
  const location = useLocation();
  const isAdminScope = location.pathname.startsWith("/admin");
  const fullBleed = /\/entregas\/?$/.test(location.pathname);
  const isAdmin = isAdminLike(user);
  const isUM = isUserManager(user);
  const [empresa, setEmpresa] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lastSync, setLastSync] = useState(new Date());
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "true"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sidebar_collapsed", String(isCollapsed)); } catch { /* noop */ }
  }, [isCollapsed]);
  // Atalho de teclado Ctrl/Cmd+B: recolhe/expande sidebar sem tirar a mão do teclado
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "b" || e.key === "B")) {
        // Ignora quando o foco está em input/textarea/select (não sequestra atalhos nativos)
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        if (["input", "textarea", "select"].includes(tag) || document.activeElement?.isContentEditable) return;
        e.preventDefault();
        setIsCollapsed((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (isAdminScope) { setEmpresa(null); return; }
    api.get("/empresa").then((r) => setEmpresa(r.data)).catch(() => { /* branding opcional */ });
  }, [location.pathname, currentEmpresaId, isAdminScope]);
  useEffect(() => {
    if (isAdminScope) return;
    const h = () => api.get("/empresa").then((r) => setEmpresa(r.data)).catch(() => { /* branding opcional */ });
    window.addEventListener("empresa-changed", h);
    return () => window.removeEventListener("empresa-changed", h);
  }, [isAdminScope]);
  useEffect(() => { const t = setInterval(() => setLastSync(new Date()), 20000); return () => clearInterval(t); }, []);

  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ atual: "", nova: "", conf: "" });
  useEffect(() => {
    const h = () => { setPwdForm({ atual: "", nova: "", conf: "" }); setPwdOpen(true); };
    window.addEventListener("open-change-password", h);
    return () => window.removeEventListener("open-change-password", h);
  }, []);
  const submitPwd = async () => {
    if (pwdForm.nova.length < 4) return toast.error("Nova senha muito curta");
    if (pwdForm.nova !== pwdForm.conf) return toast.error("Senhas não conferem");
    try {
      await api.post("/auth/change-password", { senha_atual: pwdForm.atual, nova_senha: pwdForm.nova });
      toast.success("Senha alterada com sucesso");
      setPwdOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao alterar senha"); }
  };

  const onLogout = () => { logout(); nav(slug ? `/${slug}/login` : "/login"); };

  return (
    <div className={`${fullBleed ? "h-screen overflow-hidden" : "min-h-screen"} flex flex-col bg-orange-50/30`}>
      <GlobalAdminBanner />
      <SupportBanner />
      <LicenseExpiryBanner />
      <div className="flex flex-1 min-h-0">
      {/* Sidebar desktop */}
      <aside data-testid="sidebar-desktop" className={`hidden md:flex ${isCollapsed ? "w-16" : "w-64"} bg-white border-r border-orange-100 flex-col transition-all duration-300 ease-in-out flex-shrink-0`}>
        <SidebarContent empresa={empresa} user={user} isAdmin={isAdmin} isUM={isUM} onLogout={onLogout} isAdminScope={isAdminScope} slug={slug} isCollapsed={isCollapsed} onToggle={() => setIsCollapsed((v) => !v)} />
      </aside>

      {/* Topbar mobile */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-orange-100 flex items-center gap-2 p-3 shadow-sm">
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="btn-mobile-menu"><Menu className="w-6 h-6"/></Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 flex flex-col">
            <SidebarContent empresa={empresa} user={user} isAdmin={isAdmin} isUM={isUM} onNavigate={() => setDrawerOpen(false)} onLogout={onLogout} isAdminScope={isAdminScope} slug={slug} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2 min-w-0">
          {empresa?.logo_base64 ? <img src={empresa.logo_base64} alt="logo" className="w-7 h-7 object-contain rounded"/> : <Truck className="w-5 h-5 text-orange-600"/>}
          {!isAdminScope && <EmpresaSwitcher compact />}
        </div>
      </div>

      {/* Iter 116: tela de Entregas em "tela cheia" — sem margens largas, sem scroll da página (a tabela rola por dentro) */}
      <main className={fullBleed ? "flex-1 min-h-0 p-2 md:p-3 pt-16 md:pt-2 overflow-hidden flex flex-col" : "flex-1 p-3 md:p-6 pt-16 md:pt-6 overflow-auto flex flex-col"} data-full-bleed={fullBleed}>
        <div className={`hidden md:flex justify-end text-xs text-muted-foreground ${fullBleed ? "mb-1" : "mb-2"}`} title="Dados são atualizados automaticamente a cada 20 segundos">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>Sincronizado às {lastSync.toLocaleTimeString("pt-BR")}</span>
        </div>
        <div className={fullBleed ? "flex-1 min-h-0 flex flex-col" : "flex-1"}><Outlet /></div>

        {/* Rodapé com links LGPD (fev/2026) — sem target="_blank" pra evitar
            popup blockers no PWA/mobile; página de Termos tem botão "Voltar". */}
        {!fullBleed && <footer className="mt-8 pt-4 border-t border-slate-100 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2" data-testid="layout-footer">
          <div>
            © {new Date().getFullYear()} Gestão de Entregas
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/termos"
              className="hover:text-orange-700 hover:underline flex items-center gap-1"
              data-testid="footer-link-termos"
            >
              <ScrollText className="w-3.5 h-3.5" />Termos de Uso & LGPD
            </a>
            <span className="text-slate-300">·</span>
            <a
              href="/privacidade"
              className="hover:text-orange-700 hover:underline"
              data-testid="footer-link-privacidade"
            >
              Política de Privacidade
            </a>
          </div>
        </footer>}
      </main>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-change-pwd">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-orange-600"/>Alterar minha senha</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Senha atual</Label>
              <Input type="password" value={pwdForm.atual} onChange={(e) => setPwdForm({ ...pwdForm, atual: e.target.value })} data-testid="pwd-atual"/></div>
            <div><Label>Nova senha</Label>
              <Input type="password" value={pwdForm.nova} onChange={(e) => setPwdForm({ ...pwdForm, nova: e.target.value })} data-testid="pwd-nova"/></div>
            <div><Label>Confirmar nova senha</Label>
              <Input type="password" value={pwdForm.conf} onChange={(e) => setPwdForm({ ...pwdForm, conf: e.target.value })} data-testid="pwd-conf"/></div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={() => setPwdOpen(false)}>Cancelar</Button>
              <Button onClick={submitPwd} className="bg-orange-600 hover:bg-orange-700" data-testid="pwd-submit">Alterar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
